import type { ApiClientConfig } from "./client-config.js";
import { apiCall } from "./http.js";
import type {
  EffectiveIntegrationTool,
  IntegrationCursorPage,
  IntegrationConnection,
  IntegrationConnectionMode,
  IntegrationFormSpec,
  IntegrationResourceDescriptor,
  McpAuthorizationResult,
  PlayerAiToolAccessMode,
} from "./integration.js";

/**
 * PB-1592 (EI-04) — the user's own connections.
 *
 * Separate from `integration-admin.ts` because the two surfaces have different
 * audiences and different authorization: that one is tenant-admin, this one is
 * every member managing their own credentials. Keeping them apart means a
 * member-facing page cannot accidentally import an admin call.
 */

export interface IntegrationConnectionDetail extends IntegrationConnection {
  readonly description?: string | null;
  readonly tenantIntegrationId: string;
  /** What the user consented to. Never a token, never a secret reference. */
  readonly grantedScopes: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface StartAuthorizationResult {
  readonly attemptId: string;
  /**
   * Navigate the WHOLE PAGE here, not a fetch. The provider will render its
   * consent screen and then redirect the browser back; an XHR cannot follow
   * that, and a popup adds a blocker to fail on.
   */
  readonly authorizationUrl: string;
  readonly expiresAt: string;
}

/**
 * How the callback reports back through the completion URL's query string.
 *
 * `cancelled` is its own outcome (PB-5318, **ADR-0089**), matching the
 * vocabulary `AuthorizationAttempt` below already uses — a user who declines
 * consent has not failed at anything, and reporting it as a failure put them in
 * front of a security error code for pressing Cancel.
 *
 * Adding a member to an exported union is a BREAKING export change for any
 * exhaustive consumer, which is why it carries an ADR. ADR-0089 scopes the
 * blast radius: there is one production consumer chain today
 * (`IntegrationOauthComplete.tsx`), and repo-wide typecheck is the gate that
 * makes a future one declare itself.
 */
export type IntegrationOauthOutcome = "connected" | "failed" | "cancelled";

export interface IntegrationOauthResult {
  readonly outcome: IntegrationOauthOutcome;
  readonly alreadyConnected?: boolean;
  readonly connectionId?: string;
  /** A sanitised code from the closed set. Never a provider message. */
  readonly errorCode?: string;
}

/**
 * Reads the round-trip outcome the backend put in the URL.
 *
 * Pure and exported so the page's behaviour is testable without a browser, and
 * so the query-parameter names live in exactly one place on this side.
 */
export function readOauthResult(
  search: string | URLSearchParams,
): IntegrationOauthResult | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const outcome = params.get("integrationOauth");
  if (
    outcome !== "connected" &&
    outcome !== "failed" &&
    outcome !== "cancelled"
  )
    return null;
  // A cancellation is FIELDLESS, and that is enforced here rather than assumed
  // (PB-5318). The server sends no id and no code with one, but this page is
  // public and its query string is whatever the link contained — so a crafted
  // `?integrationOauth=cancelled&errorCode=…` must not come back with a code
  // attached that the type says a cancellation never carries.
  if (outcome === "cancelled") return { outcome };
  const connectionId = params.get("connectionId");
  const errorCode = params.get("errorCode");
  return {
    outcome,
    ...(outcome === "connected" && params.get("alreadyConnected") === "true"
      ? { alreadyConnected: true }
      : {}),
    ...(connectionId === null ? {} : { connectionId }),
    ...(errorCode === null ? {} : { errorCode }),
  };
}

/** The parameters the outcome uses, so a caller can strip them after reading. */
export const OAUTH_RESULT_PARAMS = [
  "integrationOauth",
  "alreadyConnected",
  "connectionId",
  "errorCode",
] as const;

interface DataEnvelope<T> {
  readonly data: readonly T[];
}

/**
 * One integration this member may connect an account to (PB-4734).
 *
 * Narrower than `TenantIntegration` on purpose — see the server-side
 * `ConnectableIntegrationResponse`. A member chooses by name; template ids,
 * settings and policy are administrative facts and stay on the admin surface.
 */
export interface ConnectableIntegration {
  readonly id: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly protocol: "mcp" | "rest";
}

export interface AuthorizationAttempt {
  attemptId: string;
  outcome:
    | "pending"
    | "processing"
    | "connected"
    | "failed"
    | "cancelled"
    | "expired";
  expiresAt: string;
}
export function createIntegrationConnectionsApi(config: ApiClientConfig) {
  return {
    authorizationAttempt: (id: string): Promise<AuthorizationAttempt | null> =>
      apiCall(
        config,
        "GET",
        `/v1/tenant-integrations/${id}/authorization-attempt`,
      ),
    cancelAuthorization: (
      id: string,
      attemptId: string,
    ): Promise<AuthorizationAttempt | null> =>
      apiCall(
        config,
        "POST",
        `/v1/tenant-integrations/${id}/authorization-attempt/${attemptId}/cancel`,
      ),
    listMine: (): Promise<IntegrationConnectionDetail[]> =>
      apiCall<DataEnvelope<IntegrationConnectionDetail>>(
        config,
        "GET",
        "/v1/integration-connections",
      ).then((r) => [...r.data]),

    pageMine: (options: {
      readonly cursor?: string;
      readonly limit: 12 | 24 | 48;
      readonly query?: string;
      readonly transportMode?: "rest" | "mcp";
    }): Promise<IntegrationCursorPage<IntegrationConnectionDetail>> =>
      apiCall<IntegrationCursorPage<IntegrationConnectionDetail>>(
        config,
        "GET",
        "/v1/integration-connections",
        undefined,
        {
          ...(options.cursor ? { cursor: options.cursor } : {}),
          limit: options.limit,
          ...(options.query ? { q: options.query } : {}),
          ...(options.transportMode
            ? { transportMode: options.transportMode }
            : {}),
        },
      ).then((response) =>
        normalizeLegacyPage(
          response,
          options.limit,
          options.cursor,
          (row) =>
            (!options.transportMode ||
              row.transportMode === options.transportMode) &&
            (!options.query ||
              `${row.displayName} ${row.externalPrincipalName ?? ""}`
                .toLocaleLowerCase()
                .includes(options.query.toLocaleLowerCase())),
        ),
      ),

    /**
     * What the caller may connect to — granted and enabled only.
     *
     * Member-callable, unlike `createIntegrationAdminApi().listIntegrations()`,
     * which is tenant-admin only. The connections page used the admin one and
     * swallowed its 403, so every member saw an empty list however much access
     * they held.
     */
    listConnectable: (): Promise<ConnectableIntegration[]> =>
      apiCall<DataEnvelope<ConnectableIntegration>>(
        config,
        "GET",
        "/v1/connectable-integrations",
      ).then((r) => [...r.data]),

    get: (id: string): Promise<IntegrationConnectionDetail> =>
      apiCall<IntegrationConnectionDetail>(
        config,
        "GET",
        `/v1/integration-connections/${id}`,
      ),

    startAuthorization: (
      tenantIntegrationId: string,
      displayName?: string,
    ): Promise<StartAuthorizationResult> =>
      apiCall<StartAuthorizationResult, { displayName?: string }>(
        config,
        "POST",
        `/v1/tenant-integrations/${tenantIntegrationId}/connections/authorize`,
        displayName === undefined ? {} : { displayName },
      ),

    rename: (
      id: string,
      displayName: string,
    ): Promise<IntegrationConnectionDetail> =>
      apiCall<IntegrationConnectionDetail, { displayName: string }>(
        config,
        "PATCH",
        `/v1/integration-connections/${id}`,
        { displayName },
      ),

    validate: (id: string): Promise<IntegrationConnectionDetail> =>
      apiCall<IntegrationConnectionDetail>(
        config,
        "POST",
        `/v1/integration-connections/${id}/validate`,
      ),

    revoke: (id: string): Promise<IntegrationConnectionDetail> =>
      apiCall<IntegrationConnectionDetail>(
        config,
        "POST",
        `/v1/integration-connections/${id}/revoke`,
      ),

    authorizeMcp: (id: string): Promise<McpAuthorizationResult> =>
      apiCall<McpAuthorizationResult>(
        config,
        "POST",
        `/v1/integration-connections/${id}/mcp/authorize`,
      ),

    listEffectiveTools: (
      connectionId: string,
    ): Promise<EffectiveIntegrationTool[]> =>
      apiCall<DataEnvelope<EffectiveIntegrationTool>>(
        config,
        "GET",
        "/v1/integration-tools",
        undefined,
        { connectionId },
      ).then((r) => [...r.data]),

    pageEffectiveTools: (
      connectionId: string,
      options: {
        readonly cursor?: string;
        readonly limit: 25 | 50;
        readonly query?: string;
      },
    ): Promise<IntegrationCursorPage<EffectiveIntegrationTool>> =>
      apiCall<IntegrationCursorPage<EffectiveIntegrationTool>>(
        config,
        "GET",
        "/v1/integration-tools",
        undefined,
        {
          connectionId,
          ...(options.cursor ? { cursor: options.cursor } : {}),
          limit: options.limit,
          ...(options.query ? { q: options.query } : {}),
        },
      ).then((response) =>
        normalizeLegacyPage(
          response,
          options.limit,
          options.cursor,
          (row) =>
            !options.query ||
            `${row.title ?? ""} ${row.toolName} ${row.description ?? ""}`
              .toLocaleLowerCase()
              .includes(options.query.toLocaleLowerCase()),
        ),
      ),

    setPlayerAiToolAccessMode: (
      connectionId: string,
      mode: PlayerAiToolAccessMode,
    ): Promise<{ mode: PlayerAiToolAccessMode }> =>
      apiCall<
        { mode: PlayerAiToolAccessMode },
        { mode: PlayerAiToolAccessMode }
      >(
        config,
        "PUT",
        `/v1/integration-connections/${connectionId}/player-ai-tool-access`,
        { mode },
      ),

    setPlayerAiToolPreference: (
      connectionId: string,
      entryId: string,
      enabled: boolean,
    ): Promise<{
      catalogEntryId: string;
      enabled: boolean;
      schemaHash: string;
    }> =>
      apiCall(
        config,
        "PUT",
        `/v1/integration-connections/${connectionId}/player-ai-tools/${entryId}`,
        { enabled },
      ),

    /**
     * The capability's form spec, discovered live where the manifest declares
     * `form: "dynamic"`.
     *
     * The endpoint shipped with EI-04 but was not reachable from this client
     * until PB-1593, which meant a generic UI could see that a form EXISTED
     * (`ActionDescriptor.form`) and had no way to fetch it.
     */
    describeCapabilityForm: (
      id: string,
      capabilityKey: string,
      resourceId: string,
    ): Promise<IntegrationFormSpec> =>
      apiCall<IntegrationFormSpec>(
        config,
        "GET",
        `/v1/integration-connections/${id}/capabilities/${capabilityKey}/metadata`,
        undefined,
        { resourceId },
      ),

    listResources: (
      id: string,
      resourceType: string,
      parentId?: string,
    ): Promise<IntegrationResourceDescriptor[]> =>
      apiCall<DataEnvelope<IntegrationResourceDescriptor>>(
        config,
        "GET",
        `/v1/integration-connections/${id}/resources`,
        undefined,
        parentId === undefined ? { resourceType } : { resourceType, parentId },
      ).then((r) => [...r.data]),
  };
}

function normalizeLegacyPage<T>(
  response: IntegrationCursorPage<T>,
  limit: number,
  cursor: string | undefined,
  matches: (row: T) => boolean,
): IntegrationCursorPage<T> {
  if (typeof response.hasMore === "boolean") return response;
  const rows = response.data.filter(matches);
  const offset = cursor?.startsWith("legacy-")
    ? Number(cursor.slice("legacy-".length)) || 0
    : 0;
  const data = rows.slice(offset, offset + limit);
  const hasMore = offset + limit < rows.length;
  return {
    data,
    hasMore,
    nextCursor: hasMore ? `legacy-${offset + limit}` : null,
    ...(offset === 0 ? { totalCount: rows.length } : {}),
  };
}

export type IntegrationConnectionsApi = ReturnType<
  typeof createIntegrationConnectionsApi
>;

/** Re-exported so a consumer needs one import for the member-facing surface. */
export type { IntegrationConnectionMode };
